/**
 * Chat UI Store - UI-only state
 * 
 * Constitution Section 19.1: State Management Separation
 * Zustand = UI-only state only.
 * 
 * This store manages ONLY UI state:
 * - Favorites (user selections)
 * - Comparator IDs (UI selection)
 * - Conversation ID (client-generated session identifier)
 * - Comparison data (UI state for property comparison)
 * - Canvas panel state (Feature 081)
 * 
 * Server state (results, summaries, roiCard, agent_meta) is managed by React Query.
 */

import { create } from 'zustand';
import type { UIMessage } from 'ai';
import type {
  ComparePropertiesResponse,
  RoiParams,
} from '@/schemas/comparison';
import type { Mode } from '@/schemas/chat';
import type {
  PropertyCardBlock,
  ProjectGroupBlock,
  ComparisonTableBlock,
  ScenarioTableBlock,
  StrMarketBlock,
  FinancingSummaryBlock,
  ChartBlock,
  PatrimonyBlock,
} from '@/types/chat-blocks';
import {
  countUserTextMessages,
  loadConversation as loadAnonymousConversation,
  saveConversation as saveAnonymousConversation,
} from '@/features/chat/history/anonymousHistoryStore';

/**
 * Feature 081: Canvas view state
 *
 * Determines what the Canvas panel (right side) displays.
 */
export type CanvasView =
  | { kind: 'properties'; totalCount?: number }
  | { kind: 'property_detail'; propertyId: string }
  | { kind: 'finance' }
  | { kind: 'compare' }
  | { kind: 'closing_costs' }
  | { kind: 'str_market' }
  | { kind: 'financing_plan' }
  // Card O3DjiC8E (F1b): sub-view for the N units of a single model when
  // the user clicks a PropertyModelCard with ``units_available > 1`` from
  // ``PropertyResultsCanvasView``. Stacked on top of ``properties`` so the
  // back button restores the grouped list.
  | { kind: 'units'; projectId: number; modelId: number };

/**
 * Card qYc9gLFj — left sidebar (shell extensible).
 *
 * The desktop collapsed/expanded state persists across reloads in
 * localStorage so the user's layout preference sticks (criterion #2).
 * Reads are defensive: private mode / disabled storage falls back to the
 * default (expanded) without throwing.
 */
const SIDEBAR_COLLAPSED_KEY = 'inbest:chat:sidebar-collapsed';

const readSidebarCollapsed = (): boolean => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    }
  } catch {
    // SecurityError / private mode → default expanded.
  }
  return false;
};

const persistSidebarCollapsed = (collapsed: boolean): void => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
    }
  } catch {
    // Silenciosamente OK: el estado en memoria ya refleja el cambio.
  }
};

/**
 * Card j1CAmAgd — persistencia de supuestos ROI en sesión.
 *
 * Cuando el usuario ajusta los supuestos financieros (cuota inicial, tasa,
 * plazo, gastos operativos, ocupación) en un bloque ROI, esos valores deben
 * PERSISTIR para el resto de la conversación: si después pide el ROI de OTRA
 * propiedad, el cálculo arranca con los supuestos ajustados — no resetea al
 * default (criterio #3, el más valioso de la tarjeta).
 *
 * Guardamos en memoria (Zustand) + respaldo en sessionStorage keyed por
 * conversationId, igual que el saludo proactivo y el estado del sidebar. La
 * sesión es el alcance correcto: una conversación nueva (``startNewConversation``
 * regenera el conversationId) vuelve a defaults.
 *
 * Todos los campos son porcentajes/años en unidades de display (ej. 40 para
 * "40%"), consistente con lo que esperan los sliders y el endpoint
 * ``/api/v1/roi/recalculate`` (percent, no fracción).
 */
export interface RoiAssumptions {
  /** Cuota inicial en % (0-100). */
  down_pct?: number;
  /** Tasa anual EA en % (0-100). */
  rate_annual_pct?: number;
  /** Plazo del crédito en años. */
  term_years?: number;
  /** Gastos operativos en % (proxy: mantenimiento + admin). */
  opex_pct?: number;
  /** Ocupación en % — sólo relevante en modo short/Airbnb. */
  occupancy_pct?: number;
}

/** Las claves editables de ``RoiAssumptions`` (para el setter granular). */
export type RoiAssumptionKey = keyof RoiAssumptions;

const ROI_ASSUMPTIONS_KEY_PREFIX = 'inbest:chat:roi-assumptions:';

const roiAssumptionsStorageKey = (conversationId: string): string =>
  `${ROI_ASSUMPTIONS_KEY_PREFIX}${conversationId}`;

const readRoiAssumptions = (conversationId: string): RoiAssumptions => {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const raw = window.sessionStorage.getItem(
        roiAssumptionsStorageKey(conversationId),
      );
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        if (parsed && typeof parsed === 'object') {
          return parsed as RoiAssumptions;
        }
      }
    }
  } catch {
    // SecurityError / private mode / JSON inválido → arrancamos en blanco.
  }
  return {};
};

const persistRoiAssumptions = (
  conversationId: string,
  assumptions: RoiAssumptions,
): void => {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const key = roiAssumptionsStorageKey(conversationId);
      if (Object.keys(assumptions).length === 0) {
        window.sessionStorage.removeItem(key);
      } else {
        window.sessionStorage.setItem(key, JSON.stringify(assumptions));
      }
    }
  } catch {
    // Silenciosamente OK: el estado en memoria ya refleja el cambio.
  }
};

/**
 * Generate a unique conversation ID (UUID v4)
 */
const generateConversationId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback: generate UUID v4 manually
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Card 6a333ce6 — Persistencia del conversationId en localStorage.
 *
 * El conversationId es la ancla de la sesión anónima: el backend lo recibe
 * como `session_id` en cada POST a `/api/v1/chat/stream/v2`. Si el frontend
 * lo regenera en cada mount del store, perdemos:
 *   - El estado del wizard (re-emite intro tras reload).
 *   - El historial de la conversación.
 *   - El claim post-login que necesita Card #7 (Merge-on-login F2).
 *
 * Persistimos un JSON `{id, createdAt}` (no string puro) para habilitar TTL
 * futuro sin migración. El resto del store sigue viendo `conversationId`
 * como string plano. localStorage es compartido entre pestañas del mismo
 * origen, así que cumple el criterio cross-tab implícitamente.
 *
 * Robustez:
 *   - try/catch defensivo: private mode / SecurityError no rompe.
 *   - Fallback a string raw legacy (por si una versión anterior guardó solo
 *     el UUID sin wrapping).
 *   - JSON corrupto → degrada a UUID nuevo (no console.error).
 */
const CONVERSATION_STORAGE_KEY = 'inbest-chat-conversation-id';

const readConversationId = (): string | null => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const raw = window.localStorage.getItem(CONVERSATION_STORAGE_KEY);
      if (!raw) return null;
      // Forma canónica nueva: JSON `{id, createdAt}`.
      let parseError = false;
      try {
        const parsed = JSON.parse(raw) as unknown;
        if (
          parsed &&
          typeof parsed === 'object' &&
          typeof (parsed as { id?: unknown }).id === 'string' &&
          (parsed as { id: string }).id.length > 0
        ) {
          return (parsed as { id: string }).id;
        }
        // JSON válido pero sin `id` válido → tratamos como corrupto y
        // dejamos que el caller genere uno nuevo (NO caemos al fallback
        // legacy: el raw es claramente un JSON, no un UUID plano).
        return null;
      } catch {
        parseError = true;
      }
      // Solo si el parseo falló consideramos el fallback legacy: una versión
      // anterior pudo haber guardado el UUID como string puro. Re-persistimos
      // en el shape canónico desde el factory.
      if (parseError && typeof raw === 'string' && raw.length > 0) {
        return raw;
      }
    }
  } catch {
    // SecurityError / private mode → caller genera uno nuevo.
  }
  return null;
};

const persistConversationId = (id: string): void => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const payload = JSON.stringify({ id, createdAt: Date.now() });
      window.localStorage.setItem(CONVERSATION_STORAGE_KEY, payload);
    }
  } catch {
    // Silenciosamente OK: el estado en memoria ya refleja el cambio.
  }
};

/**
 * Chat UI State
 *
 * Only UI-related state. Server state is in React Query.
 */
interface ChatUIState {
  // Conversation ID (client-generated, UI concern)
  conversationId: string;

  // UI selections
  favorites: number[];
  openComparatorIds: number[];

  // Feature 056: Comparison state
  comparisonData: ComparePropertiesResponse | null;
  isComparing: boolean;
  comparisonError: string | null;
  roiParams: RoiParams;
  savedComparisonId: string | null;

  // Feature 065: Mode switching UI state
  currentMode: Mode;
  previousMode: Mode | null;
  modeTransitionToast: { message: string; mode: string } | null;

  // Feature 081: Canvas panel state
  canvasOpen: boolean;
  canvasView: CanvasView;
  canvasViewMode: 'card' | 'compact';

  // Card qYc9gLFj: left sidebar (shell extensible)
  // - `sidebarCollapsed` is the desktop rail state, persisted to localStorage.
  // - `sidebarMobileOpen` drives the mobile drawer overlay (not persisted —
  //   it should always start closed on a fresh mount).
  sidebarCollapsed: boolean;
  sidebarMobileOpen: boolean;

  /**
   * Card iihkH2tK F1 — historial anónimo (localStorage).
   *
   * `loadedFromHistoryId` marca la convo activa como "vino del historial",
   * para que `startNewConversation` haga UPSERT por id (no genere un slot
   * nuevo cada vez que el user navega entre historial → nueva).
   *
   * `pendingHistoryMessages` es el snapshot que `loadConversationFromHistory`
   * deja para que el `ChatPanelV2` recién-remontado lo inyecte en `useChat`
   * vía `setMessages()`. Se consume con `consumePendingHistoryMessages()`.
   */
  loadedFromHistoryId: string | null;
  pendingHistoryMessages: UIMessage[] | null;

  /**
   * Card tlTrLe7t (Phase 1) — landmark de "conversación actual (nueva)".
   *
   * `startNewConversation()` sube este flag a `true` para que el sidebar
   * `HISTORIAL` renderice una entry sintética activa arriba de la lista
   * ("estoy acá ahora"). El flag se baja a `false` cuando:
   *   - El usuario envía el primer mensaje (via `markConversationActive`).
   *   - El usuario carga una conversación del historial local o del backend.
   *   - El store se resetea completamente (por ej. logout).
   *
   * NO se persiste. Un reload arranca en `false` porque el conversationId
   * rehidratado ya representa una conversación con historia (aunque sea
   * corta) — no una "recién estrenada" en el sentido de UX.
   */
  currentSessionIsFresh: boolean;

  // v2 typed-blocks cards — keyed by property_id so CanvasPanel can look up
  // detail without going through the old getConversationHistory API.
  v2CardMap: Record<number, PropertyCardBlock>;
  // Ordered property IDs preserving SSE stream order (JS objects sort numeric
  // keys numerically, losing insertion order).
  v2CardOrder: number[];
  setV2Cards: (cards: PropertyCardBlock[]) => void;

  /**
   * Card O3DjiC8E (F1b): cache of ProjectGroupBlock items received from the
   * SSE stream so ``PropertyResultsCanvasView`` can render the grouped
   * canvas (project headers + model cards) without re-deriving the
   * aggregation in the frontend. Keyed by ``project_id`` to dedupe across
   * the same conversation.
   */
  v2ProjectGroupMap: Record<number, ProjectGroupBlock>;
  /** Insertion order of project_id keys preserving SSE stream order. */
  v2ProjectGroupOrder: number[];
  setV2ProjectGroups: (groups: ProjectGroupBlock[]) => void;

  // S4/S3 Canvas data — typed-block payloads hydrated by inline
  // renderers from the SSE stream.
  strMarketBlock: StrMarketBlock | null;
  financingPlanBlock: FinancingSummaryBlock | null;
  setStrMarketBlock: (block: StrMarketBlock) => void;
  setFinancingPlanBlock: (block: FinancingSummaryBlock) => void;

  // S1/S2 Canvas data — hydrated by inline block renderers with the
  // typed-block payload itself. Canvas views consume these directly,
  // no legacy CompareDetails / ClosingCostsDetails / FinanceDetails
  // shape conversion in between.
  comparisonViewBlock: ComparisonTableBlock | null;
  closingCostsViewBlock: ScenarioTableBlock | null;
  financeViewBlock: ScenarioTableBlock | null;
  /**
   * Canvas FinanceView ROI charts — hydrated by `ChartBlockRenderer` when
   * a chart's caption matches one of the four ROI redesign captions
   * (card ryJoLGnm). The renderer skips inline rendering for these blocks
   * so the charts only appear in Canvas. Indexed by caption so successive
   * `calculate_roi` calls overwrite per-slot rather than appending.
   */
  financeChartsBlocks: Record<string, ChartBlock>;
  /**
   * SPEC 42v1cSyH H5 (card FUjaXmfj) — payload patrimonial que hidrata la
   * Superficie 2 (Canvas FinanceView + STRMarketView). El renderer de
   * Superficie 1 (``PatrimonyBlockRenderer``, hija H4 ZFOGLiUn) lo escribe
   * cuando el chat-service emite el ``patrimony_block``; el CanvasPanel lo
   * lee y lo pasa a las views expandidas. `null` cuando la conversación
   * todavía no invocó la tool ``patrimony_projection``.
   */
  patrimonyBlock: PatrimonyBlock | null;
  setComparisonViewBlock: (block: ComparisonTableBlock) => void;
  setClosingCostsViewBlock: (block: ScenarioTableBlock) => void;
  setFinanceViewBlock: (block: ScenarioTableBlock) => void;
  setFinanceChartBlock: (block: ChartBlock) => void;
  clearFinanceChartsBlocks: () => void;
  setPatrimonyBlock: (block: PatrimonyBlock) => void;

  // Pending chat action — Canvas-side action chips (Calcular ROI #N,
  // Contactar, Ver proyecto, etc.) set this to a chat message; the
  // active chat panel watches it and dispatches via the v2 transport,
  // then clears it.
  pendingChatAction: string | null;
  setPendingChatAction: (text: string | null) => void;

  /**
   * Card j1CAmAgd — supuestos ROI ajustados por el usuario, persistidos por
   * sesión (criterio #3). Cualquier bloque ROI (RoiWhatIf / panel inline del
   * RoiBlock) siembra sus sliders desde acá y escribe de vuelta en cada
   * cambio, de modo que el ROI de la SIGUIENTE propiedad respete los ajustes.
   * Empieza vacío (cada slider cae a su default del bloque) y se llena a
   * medida que el usuario mueve sliders o aplica una sugerencia.
   */
  roiAssumptions: RoiAssumptions;
  /** Mergea un parche de supuestos (campos undefined se ignoran). */
  setRoiAssumptions: (patch: Partial<RoiAssumptions>) => void;
  /** Setea un único supuesto (atajo para el onChange de un slider). */
  setRoiAssumption: (key: RoiAssumptionKey, value: number) => void;
  /** Vuelve los supuestos a default (criterio #4) — limpia memoria + storage. */
  resetRoiAssumptions: () => void;

  // Feature 086: Lead modal (shared between Canvas + Chat)
  // `phone` resolved as project.phone || company.phone for the wa.me link.
  // `projectName` is preferred over `propertyName` in the wa.me message body.
  leadModal: {
    propertyId: number;
    propertyName: string;
    phone?: string | null;
    projectName?: string | null;
  } | null;

  /**
   * v2 card map — caches PropertyCardBlock items received from the SSE
   * blocks stream so PropertyDetailView can look them up by property_id.
   * Keyed by String(property_id). Cleared on conversation reset.
   */
  v2CardMap: Record<string, PropertyCardBlock>;

  // Actions
  toggleFavorite: (id: number) => void;
  setComparator: (ids: number[]) => void;
  reset: () => void;
  setConversationId: (id: string) => void;

  // Feature 065: Mode switching actions
  setCurrentMode: (mode: Mode) => void;
  dismissModeTransitionToast: () => void;

  // Feature 081: Canvas actions
  openCanvas: (view: CanvasView) => void;
  closeCanvas: () => void;
  setCanvasView: (view: CanvasView) => void;
  setCanvasViewMode: (mode: 'card' | 'compact') => void;

  // Card qYc9gLFj: sidebar actions
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  openSidebarMobile: () => void;
  closeSidebarMobile: () => void;
  /**
   * Start a fresh chat session cleanly (criterion #6). Wraps `reset()` so the
   * conversationId regenerates (which also re-arms the proactive greeting,
   * keyed by conversationId) and closes the mobile drawer if it was open.
   *
   * Card iihkH2tK F1: ANTES del reset, si hay un `historySnapshotGetter`
   * registrado y la convo activa tiene >=1 mensaje del usuario, snapshot +
   * save al historial anónimo (localStorage). Si la convo venía cargada de
   * historial (`loadedFromHistoryId !== null`), el `save` hace UPSERT por id.
   */
  startNewConversation: () => void;

  /**
   * Card iihkH2tK F1 — cargar una conversación del historial anónimo.
   *
   * El sidebar la invoca con el id del entry clickeado. La acción:
   *   1. Lee el entry del localStorage (anonymousHistoryStore).
   *   2. Si no existe → no-op (caller debería refrescar la lista).
   *   3. Setea `conversationId` al id de la entry (el remount idiomático
   *      del `ChatPage` keyed por conversationId regenera el `ChatPanelV2`).
   *   4. Marca `loadedFromHistoryId = id` para que la próxima "Nueva
   *      conversación" UPSERTee en vez de duplicar.
   *   5. Deja `pendingHistoryMessages` = entry.messages para que el panel
   *      recién-remontado lo inyecte vía `useChat.setMessages()`.
   *   6. Cierra el drawer mobile (acción self-contained).
   */
  loadConversationFromHistory: (id: string) => void;

  /**
   * Card ZWy2H37A — cargar una conversación linkeada al backend (user
   * autenticado). A diferencia de ``loadConversationFromHistory``, acá no
   * hay snapshot local del que hidratar — el ``ChatPanelV2`` se remonta con
   * el nuevo ``conversationId`` y su effect ``dE0qi1L9`` dispara el fetch
   * de ``GET /sessions/{sid}/history`` automáticamente.
   *
   * Diseño:
   *   1. Cambiamos ``conversationId`` (persistImos en localStorage) — el
   *      key del padre ``ChatPage`` remonta ``ChatPanelV2`` con el id nuevo.
   *   2. Limpiamos ``pendingHistoryMessages`` (asegura que el path server
   *      gane sobre el path local ausente) + limpiamos ``loadedFromHistoryId``
   *      (esta convo NO viene del localStorage).
   *   3. Limpiamos caches del Canvas (mismo motivo que en la variante local).
   *   4. Cierra el drawer mobile.
   *
   * NO llamamos ``reset()`` porque queremos preservar el conversationId
   * nuevo y NO regenerarlo. Es un cambio de contexto, no un "nuevo chat".
   */
  loadServerConversation: (sessionId: string) => void;

  /**
   * Card tKchYaSf (Fase 2B) — resetea la conversación activa cuando el user
   * borra la sesión actual. NO borra en el backend (el sidebar delega en el
   * transport), sólo limpia el state UI y arranca una nueva conversación.
   *
   * Idempotente: si el ``sessionId`` a borrar NO es el activo, la acción
   * es no-op (el sidebar sólo remueve la entry sin resetear al user).
   */
  handleSessionDeletedIfActive: (deletedSessionId: string) => void;

  /**
   * Card iihkH2tK F1 — drena `pendingHistoryMessages` y lo limpia atómicamente.
   * Lo llama `ChatPanelV2` desde un `useEffect` on-mount; al consumir, el
   * estado vuelve a `null` para no re-inyectar en re-renders posteriores.
   */
  consumePendingHistoryMessages: () => UIMessage[] | null;

  /**
   * Card tlTrLe7t (Phase 1) — baja el flag `currentSessionIsFresh` a false.
   *
   * Se llama desde `ChatPanelV2` cuando la conversación deja de estar
   * "recién estrenada" (typicamente al observar que hay mensajes en el
   * `useChat` state). Idempotente — llamarlo con el flag ya en `false`
   * no hace nada.
   */
  markConversationActive: () => void;

  /**
   * Card iihkH2tK F1 — registra un callback que devuelve el snapshot actual
   * de `messages` del `useChat` activo. Se invoca desde `startNewConversation`
   * para poder guardar al historial antes del reset.
   *
   * Pasar `null` desregistra (cleanup al desmontar el panel).
   */
  registerHistorySnapshotGetter: (
    getter: (() => UIMessage[]) | null,
  ) => void;

  // Feature 086: Lead modal actions
  openLeadModal: (
    propertyId: number,
    propertyName: string,
    options?: { phone?: string | null; projectName?: string | null },
  ) => void;
  closeLeadModal: () => void;

  // Feature 056: Comparison actions
  setComparisonData: (data: ComparePropertiesResponse | null) => void;
  setIsComparing: (isComparing: boolean) => void;
  setComparisonError: (error: string | null) => void;
  setRoiParams: (params: Partial<RoiParams>) => void;
  setSavedComparisonId: (id: string | null) => void;
  clearComparisonData: () => void;

  /** Register PropertyCardBlock items from the v2 SSE stream for detail view lookup. */
  registerV2Cards: (cards: PropertyCardBlock[]) => void;

  /**
   * Card JRJi2TqD (QA iter#1): wipe the v2 typed-blocks caches that feed
   * the Canvas grouped/flat result views. Called by ``CanvasPanel`` right
   * before a toolbar-driven re-search so a previous result set never
   * lingers when the new search returns 0 (the empty state can then
   * render correctly).
   */
  clearV2SearchResults: () => void;
}

export const useChatUIStore = create<ChatUIState>((set, get) => {
  // Card 6a333ce6 — rehidratamos el conversationId desde localStorage si
  // existe (mismo UUID tras reload, F5, cierre/reapertura del tab). Si no,
  // generamos uno nuevo y lo persistimos inmediatamente para que el primer
  // POST al backend ya use el id estable.
  const existingConversationId = readConversationId();
  const conversationId = existingConversationId ?? generateConversationId();
  if (!existingConversationId) {
    persistConversationId(conversationId);
  } else {
    // Si venía del fallback legacy (string raw), re-persistImos en el
    // shape canónico `{id, createdAt}` para futuros reads.
    persistConversationId(conversationId);
  }

  /**
   * Card iihkH2tK F1 — getter registrado por `ChatPanelV2` que devuelve los
   * `messages` actuales del `useChat`. Lo invoca `startNewConversation` antes
   * del reset para snapshot al historial. Es una variable de módulo (closure)
   * en vez de state Zustand porque no necesita reactividad — solo es un puente
   * de lectura puntual.
   */
  let historySnapshotGetter: (() => UIMessage[]) | null = null;

  return {
    conversationId,
    favorites: [],
    openComparatorIds: [],
    
    // Feature 056: Comparison state
    comparisonData: null,
    isComparing: false,
    comparisonError: null,
    roiParams: {
      mode: 'long' as const,
      down_pct: 0.3,
      rate_annual: 0.13,
      term_years: 20,
    },
    savedComparisonId: null,

    // Feature 065: Mode switching state
    currentMode: 'search' as Mode,
    previousMode: null,
    modeTransitionToast: null,

    // Feature 081: Canvas panel state
    canvasOpen: false,
    canvasView: { kind: 'properties' } as CanvasView,
    canvasViewMode: 'card' as const,

    // Card qYc9gLFj: sidebar state (collapsed persisted, mobile always closed on mount)
    sidebarCollapsed: readSidebarCollapsed(),
    sidebarMobileOpen: false,

    // Card iihkH2tK F1: historial anónimo — slots inicialmente vacíos. El
    // store NO se rehidrata desde localStorage al construirse porque la
    // convo activa puede o no estar en el historial; solo se popula cuando
    // `loadConversationFromHistory` lo llena explícitamente.
    loadedFromHistoryId: null,
    pendingHistoryMessages: null,

    // Card tlTrLe7t (Phase 1): la sesión rehidratada NO es "fresca" — sólo
    // `startNewConversation()` la marca así en el mismo runtime del store.
    currentSessionIsFresh: false,

    // v2 typed-blocks card cache
    v2CardMap: {},
    v2CardOrder: [],

    // O3DjiC8E: ProjectGroup cache for the grouped canvas view
    v2ProjectGroupMap: {},
    v2ProjectGroupOrder: [],

    // S4/S3 Canvas data
    strMarketBlock: null,
    financingPlanBlock: null,

    // S1/S2 Canvas data
    comparisonViewBlock: null,
    closingCostsViewBlock: null,
    financeViewBlock: null,
    // Card ryJoLGnm: ROI charts hydrated from ChartBlocks via caption match.
    financeChartsBlocks: {},
    // SPEC 42v1cSyH H5 (card FUjaXmfj): patrimony block hydrated by the
    // chat renderer so the Canvas Superficie 2 can consume it.
    patrimonyBlock: null,

    // Pending action queued for chat dispatch
    pendingChatAction: null,

    // Card j1CAmAgd: supuestos ROI persistidos — rehidratados del
    // sessionStorage para la conversación actual (sobrevive a un reload de
    // la pestaña dentro de la misma sesión).
    roiAssumptions: readRoiAssumptions(conversationId),

    // Feature 086: Lead modal
    leadModal: null,

    // v2 card map — populated during streaming, cleared on reset
    v2CardMap: {},

    toggleFavorite: (id: number) => {
      const favorites = new Set(get().favorites);
      if (favorites.has(id)) {
        favorites.delete(id);
      } else {
        favorites.add(id);
      }
      set({ favorites: Array.from(favorites) });
    },
    
    setComparator: (ids: number[]) => {
      // Feature 056: Limit to 4 items for comparison (was 3)
      set({ openComparatorIds: ids.slice(0, 4) });
    },
    
    setConversationId: (id: string) => {
      // Card 6a333ce6 — persistImos al setear para que un setter externo
      // (ej. claim post-login en Card #7) deje el id estable en storage.
      persistConversationId(id);
      set({ conversationId: id });
    },
    
    reset: () => {
      // Card j1CAmAgd — limpiamos los supuestos persistidos de la conversación
      // saliente antes de regenerar el id, así sessionStorage no acumula claves
      // huérfanas y la nueva conversación arranca en defaults.
      persistRoiAssumptions(get().conversationId, {});
      // Card 6a333ce6 — generamos el id nuevo y lo persistImos a localStorage
      // para que "Nueva conversación" sobreviva a un reload posterior.
      const nextConversationId = generateConversationId();
      persistConversationId(nextConversationId);
      set({
        conversationId: nextConversationId,
        favorites: [],
        openComparatorIds: [],
        // Card j1CAmAgd: nueva conversación → supuestos en default.
        roiAssumptions: {},
        // Feature 065: Reset mode state
        currentMode: 'search' as Mode,
        previousMode: null,
        modeTransitionToast: null,
        // Feature 081: Reset canvas state
        canvasOpen: false,
        canvasView: { kind: 'properties' } as CanvasView,
        canvasViewMode: 'card' as const,
        // Feature 086: Reset lead modal
        leadModal: null,
        v2CardMap: {},
        v2CardOrder: [],
        v2ProjectGroupMap: {},
        v2ProjectGroupOrder: [],
        // S4/S3: Reset canvas data
        strMarketBlock: null,
        financingPlanBlock: null,
        // S1/S2: Reset canvas data
        comparisonViewBlock: null,
        closingCostsViewBlock: null,
        financeViewBlock: null,
        financeChartsBlocks: {},
        patrimonyBlock: null,
        pendingChatAction: null,
        // Feature 056: Reset comparison state
        comparisonData: null,
        isComparing: false,
        comparisonError: null,
        roiParams: {
          mode: 'long' as const,
          down_pct: 0.3,
          rate_annual: 0.13,
          term_years: 20,
        },
        savedComparisonId: null,
        // Card iihkH2tK F1: limpiar markers de historial — la nueva convo
        // arranca limpia. `pendingHistoryMessages` SOLO se setea en
        // `loadConversationFromHistory`, así que limpiarlo acá garantiza
        // que un reset accidental no se pise con un restore viejo.
        loadedFromHistoryId: null,
        pendingHistoryMessages: null,
        // Card tlTrLe7t (Phase 1): un `reset()` "pelado" (no via
        // startNewConversation) NO deja la sesión como fresh — mantiene
        // el default false. `startNewConversation` lo sube a `true`
        // explícitamente después de este reset.
        currentSessionIsFresh: false,
      });
    },

    // Feature 065: Mode switching actions
    setCurrentMode: (mode: Mode) => {
      const current = get().currentMode;
      if (current !== mode) {
        const labels: Record<string, string> = {
          search: 'Buscar propiedades',
          finance: 'Análisis de inversión',
          compare: 'Comparar propiedades',
          closing_costs: 'Costos de cierre',
        };
        set({
          currentMode: mode,
          previousMode: current,
          modeTransitionToast: {
            message: labels[mode] ?? mode,
            mode,
          },
        });
        setTimeout(() => {
          if (get().modeTransitionToast?.mode === mode) {
            set({ modeTransitionToast: null });
          }
        }, 3000);
      }
    },
    dismissModeTransitionToast: () => set({ modeTransitionToast: null }),

    setV2Cards: (cards: PropertyCardBlock[]) =>
      set((state) => {
        const updated = { ...state.v2CardMap };
        // Build new order from incoming cards, preserving SSE stream order.
        // Each call replaces the full order (a new search = new results).
        const order: number[] = [];
        for (const card of cards) {
          updated[card.property_id] = card;
          order.push(card.property_id);
        }
        return { v2CardMap: updated, v2CardOrder: order };
      }),

    // O3DjiC8E: hydrate ProjectGroupBlock cache from the SSE stream. Replaces
    // the full order on each call so a new search resets the grouped view.
    setV2ProjectGroups: (groups: ProjectGroupBlock[]) =>
      set((state) => {
        const updated = { ...state.v2ProjectGroupMap };
        const order: number[] = [];
        for (const group of groups) {
          updated[group.project_id] = group;
          order.push(group.project_id);
        }
        return { v2ProjectGroupMap: updated, v2ProjectGroupOrder: order };
      }),

    setStrMarketBlock: (block: StrMarketBlock) => set({ strMarketBlock: block }),
    setFinancingPlanBlock: (block: FinancingSummaryBlock) => set({ financingPlanBlock: block }),
    setComparisonViewBlock: (block: ComparisonTableBlock) => set({ comparisonViewBlock: block }),
    setClosingCostsViewBlock: (block: ScenarioTableBlock) => set({ closingCostsViewBlock: block }),
    setFinanceViewBlock: (block: ScenarioTableBlock) => set({ financeViewBlock: block }),
    // Card ryJoLGnm: per-caption upsert. The caption is the wire contract
    // identifier — see `_CAPTION_*` constants in the chat-service mapper.
    setFinanceChartBlock: (block: ChartBlock) =>
      set((state) => ({
        financeChartsBlocks: {
          ...state.financeChartsBlocks,
          [block.caption]: block,
        },
      })),
    clearFinanceChartsBlocks: () => set({ financeChartsBlocks: {} }),
    // SPEC 42v1cSyH H5 (card FUjaXmfj): hidratación del payload patrimonial
    // que emite chat-service (tool ``patrimony_projection``). Superficie 1
    // (PatrimonyBlockRenderer) llama este setter dentro de useEffect para
    // que Superficie 2 (FinanceView / STRMarketView) pueda leerlo sin
    // duplicar la lógica de extracción.
    setPatrimonyBlock: (block: PatrimonyBlock) => set({ patrimonyBlock: block }),
    setPendingChatAction: (text: string | null) => set({ pendingChatAction: text }),

    // Card j1CAmAgd — supuestos ROI persistidos por sesión.
    setRoiAssumptions: (patch: Partial<RoiAssumptions>) =>
      set((state) => {
        // Filtramos undefined para no pisar valores ya guardados con vacíos.
        const next: RoiAssumptions = { ...state.roiAssumptions };
        for (const [k, v] of Object.entries(patch)) {
          if (v != null && Number.isFinite(v)) {
            next[k as RoiAssumptionKey] = v as number;
          }
        }
        persistRoiAssumptions(state.conversationId, next);
        return { roiAssumptions: next };
      }),
    setRoiAssumption: (key: RoiAssumptionKey, value: number) =>
      set((state) => {
        if (!Number.isFinite(value)) return {};
        const next: RoiAssumptions = { ...state.roiAssumptions, [key]: value };
        persistRoiAssumptions(state.conversationId, next);
        return { roiAssumptions: next };
      }),
    resetRoiAssumptions: () =>
      set((state) => {
        persistRoiAssumptions(state.conversationId, {});
        return { roiAssumptions: {} };
      }),

    // Feature 081: Canvas actions
    openCanvas: (view: CanvasView) => set({ canvasOpen: true, canvasView: view }),
    closeCanvas: () => set({ canvasOpen: false }),
    setCanvasView: (view: CanvasView) => set({ canvasView: view }),
    setCanvasViewMode: (mode: 'card' | 'compact') => set({ canvasViewMode: mode }),

    // Card qYc9gLFj: sidebar actions
    toggleSidebar: () => {
      const next = !get().sidebarCollapsed;
      persistSidebarCollapsed(next);
      set({ sidebarCollapsed: next });
    },
    setSidebarCollapsed: (collapsed: boolean) => {
      persistSidebarCollapsed(collapsed);
      set({ sidebarCollapsed: collapsed });
    },
    openSidebarMobile: () => set({ sidebarMobileOpen: true }),
    closeSidebarMobile: () => set({ sidebarMobileOpen: false }),
    startNewConversation: () => {
      // Card iihkH2tK F1 — snapshot a historial ANTES del reset.
      //
      // `historySnapshotGetter` lo registra `ChatPanelV2` on-mount (puente al
      // state interno de `useChat`). Si está disponible y la convo tiene
      // >=1 mensaje del user, guardamos al localStorage. El gating de
      // user-count vive acá (no en el helper) porque solo acá sabemos qué
      // cuenta como "vacío" (greeting + wizard intro != user input).
      const state = get();
      try {
        if (typeof historySnapshotGetter === 'function') {
          const snapshot = historySnapshotGetter();
          if (Array.isArray(snapshot) && countUserTextMessages(snapshot) >= 1) {
            saveAnonymousConversation(state.conversationId, snapshot);
          }
        }
      } catch {
        // No queremos que un fallo en el snapshot bloquee la "Nueva
        // conversación" — degradamos silenciosamente (en el peor caso
        // perdemos esa convo del historial pero el reset corre normal).
      }

      // `reset()` regenerates conversationId + clears canvas/cards/etc.
      // Closing the mobile drawer keeps the action self-contained so the
      // caller doesn't have to remember to dismiss it.
      //
      // Card tlTrLe7t (Phase 1): marcamos la sesión como fresh DESPUÉS del
      // reset (que la baja a false por default), para que el sidebar
      // renderice el landmark "estoy acá ahora" arriba de la lista.
      get().reset();
      set({ sidebarMobileOpen: false, currentSessionIsFresh: true });
    },

    loadConversationFromHistory: (id: string) => {
      // Lee el entry; si no existe, no-op (el sidebar debería refrescar).
      const entry = loadAnonymousConversation(id);
      if (!entry) return;

      // Cambiamos el conversationId (lo persistImos, igual que `setConversationId`)
      // PERO sin pasar por el setter público para evitar dispatchear listeners
      // intermedios antes de tener `pendingHistoryMessages` listo — todo en un
      // único `set()` atómico.
      persistConversationId(id);
      set({
        conversationId: id,
        loadedFromHistoryId: id,
        pendingHistoryMessages: entry.messages,
        // Cerrar el drawer mobile mantiene la acción self-contained.
        sidebarMobileOpen: false,
        // Card tlTrLe7t (Phase 1): cargar una convo existente ya NO deja la
        // sesión como fresh — el landmark desaparece.
        currentSessionIsFresh: false,
        // Limpiamos caches del Canvas para que la convo restaurada no herede
        // typed-blocks de la convo anterior (que serían inconsistentes con
        // los messages que estamos restaurando — el panel re-poblará desde
        // el stream de blocks de la convo cargada si el user interactúa).
        v2CardMap: {},
        v2CardOrder: [],
        v2ProjectGroupMap: {},
        v2ProjectGroupOrder: [],
        strMarketBlock: null,
        financingPlanBlock: null,
        comparisonViewBlock: null,
        closingCostsViewBlock: null,
        financeViewBlock: null,
        financeChartsBlocks: {},
        patrimonyBlock: null,
        canvasOpen: false,
        canvasView: { kind: 'properties' } as CanvasView,
      });
    },

    loadServerConversation: (sessionId: string) => {
      // Card ZWy2H37A — clickear una entry del backend cambia el
      // conversationId; el ChatPanelV2 se remonta y el effect dE0qi1L9
      // hace el fetch de /history automáticamente. NO necesitamos
      // pendingHistoryMessages porque no hay snapshot local.
      if (!sessionId) return;
      persistConversationId(sessionId);
      set({
        conversationId: sessionId,
        // Server-loaded convos NO vienen del localStorage — limpiamos el
        // flag para que "Nueva conversación" no haga UPSERT accidental.
        loadedFromHistoryId: null,
        pendingHistoryMessages: null,
        sidebarMobileOpen: false,
        // Card tlTrLe7t (Phase 1): mismo motivo que en el path local — cargar
        // una convo del backend NO deja la sesión como fresh.
        currentSessionIsFresh: false,
        // Mismo cleanup de Canvas que la variante local: la convo nueva
        // no debería heredar typed-blocks de la anterior.
        v2CardMap: {},
        v2CardOrder: [],
        v2ProjectGroupMap: {},
        v2ProjectGroupOrder: [],
        strMarketBlock: null,
        financingPlanBlock: null,
        comparisonViewBlock: null,
        closingCostsViewBlock: null,
        financeViewBlock: null,
        financeChartsBlocks: {},
        patrimonyBlock: null,
        canvasOpen: false,
        canvasView: { kind: 'properties' } as CanvasView,
      });
    },

    handleSessionDeletedIfActive: (deletedSessionId: string) => {
      // Card tKchYaSf (Fase 2B) — cuando el user borra la conversación
      // actualmente activa, disparamos el flujo de "Nueva conversación"
      // para que el ChatPanelV2 se remonte con un conversationId nuevo
      // (evita mostrar mensajes de una session que ya no existe upstream).
      // Idempotente cuando la deleted no es la activa.
      if (!deletedSessionId) return;
      if (get().conversationId !== deletedSessionId) return;
      get().startNewConversation();
    },

    consumePendingHistoryMessages: () => {
      const pending = get().pendingHistoryMessages;
      if (pending === null) return null;
      set({ pendingHistoryMessages: null });
      return pending;
    },

    // Card tlTrLe7t (Phase 1): idempotente — sólo hace `set` si el flag
    // estaba en `true` para no disparar re-renders innecesarios.
    markConversationActive: () => {
      if (get().currentSessionIsFresh) {
        set({ currentSessionIsFresh: false });
      }
    },

    registerHistorySnapshotGetter: (
      getter: (() => UIMessage[]) | null,
    ) => {
      historySnapshotGetter = getter;
    },

    // Feature 086: Lead modal actions
    openLeadModal: (propertyId, propertyName, options) =>
      set({
        leadModal: {
          propertyId,
          propertyName,
          phone: options?.phone ?? null,
          projectName: options?.projectName ?? null,
        },
      }),
    closeLeadModal: () => set({ leadModal: null }),

    // Feature 056: Comparison actions
    setComparisonData: (data: ComparePropertiesResponse | null) => {
      set({ comparisonData: data });
    },
    
    setIsComparing: (isComparing: boolean) => {
      set({ isComparing });
    },
    
    setComparisonError: (error: string | null) => {
      set({ comparisonError: error });
    },
    
    setRoiParams: (params: Partial<RoiParams>) => {
      set((state) => ({
        roiParams: { ...state.roiParams, ...params },
      }));
    },
    
    setSavedComparisonId: (id: string | null) => {
      set({ savedComparisonId: id });
    },
    
    clearComparisonData: () => {
      set({
        comparisonData: null,
        isComparing: false,
        comparisonError: null,
        savedComparisonId: null,
      });
    },

    registerV2Cards: (cards: PropertyCardBlock[]) => {
      if (!cards.length) return;
      set((state) => {
        const updated = { ...state.v2CardMap };
        for (const card of cards) {
          updated[String(card.property_id)] = card;
        }
        return { v2CardMap: updated };
      });
    },

    // Card JRJi2TqD (QA iter#1): clear v2 typed-block caches without
    // touching anything else (conversation id, comparators, canvas view,
    // etc. stay intact). Used by the Canvas toolbar before a re-search.
    clearV2SearchResults: () =>
      set({
        v2CardMap: {},
        v2CardOrder: [],
        v2ProjectGroupMap: {},
        v2ProjectGroupOrder: [],
      }),
  };
});
